import { useEffect, useState } from "react";
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameMonth, isSameDay, addMonths, subMonths, getDay, startOfWeek, endOfWeek,
} from "date-fns";
import { ChevronLeft, ChevronRight, DollarSign, Receipt, AlertTriangle, Calendar } from "lucide-react";
import Modal from "../components/Modal";
import { getPaychecks, getBills } from "../utils/api";
import { getMonthEvents, getWeeklyCashFlow, fmt } from "../utils/calculations";

const EVENT_COLORS = {
  paycheck: { dot: "bg-blue-500",   badge: "bg-blue-100 text-blue-800",   label: "Income"  },
  bill:     { dot: "bg-red-500",    badge: "bg-red-100 text-red-800",     label: "Bill"    },
};

const CATEGORY_DOTS = {
  Housing: "bg-purple-500", Utilities: "bg-yellow-500", Subscriptions: "bg-pink-500",
  Transportation: "bg-orange-500", Groceries: "bg-green-500", Insurance: "bg-teal-500",
  Entertainment: "bg-indigo-500", Medical: "bg-red-600", Other: "bg-gray-500",
};

export default function CalendarPage() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [paychecks, setPaychecks] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    Promise.all([getPaychecks(), getBills()])
      .then(([p, b]) => { setPaychecks(p); setBills(b); })
      .finally(() => setLoading(false));
  }, []);

  const year  = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const events = getMonthEvents(year, month, paychecks, bills, []);
  const weeklyFlow = getWeeklyCashFlow(events);

  // Build calendar grid (6 rows × 7 cols)
  const monthStart = startOfMonth(currentMonth);
  const monthEnd   = endOfMonth(currentMonth);
  const calStart   = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sun
  const calEnd     = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });

  function eventsOnDay(day) {
    return events.filter((ev) => isSameDay(ev.date, day));
  }

  const totalIncome   = events.filter((e) => e.amount > 0).reduce((s, e) => s + e.amount, 0);
  const totalExpenses = events.filter((e) => e.amount < 0).reduce((s, e) => s + Math.abs(e.amount), 0);

  const selectedEvents = selectedDay ? eventsOnDay(selectedDay) : [];

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Payday Calendar</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Visualize when money comes in and goes out</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Month navigation + summary */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
              <div className="text-center">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {format(currentMonth, "MMMM yyyy")}
                </h2>
                <div className="flex gap-4 mt-1 text-sm justify-center">
                  <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                    <DollarSign className="w-3.5 h-3.5" /> {fmt(totalIncome)} in
                  </span>
                  <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                    <Receipt className="w-3.5 h-3.5" /> {fmt(totalExpenses)} out
                  </span>
                  <span className={`font-semibold ${totalIncome - totalExpenses >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                    Net: {fmt(totalIncome - totalExpenses)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            {/*
              Mobile agenda view (hidden at md+).
              Why Option A (agenda list) over a swipeable week view:
              - No gesture library / extra bundle weight.
              - Money events read naturally as a scrollable timeline.
              - Touch targets are full-width rows, easy to hit.
              - Matches the "bills/paychecks as a list" mental model users already have.
            */}
            <div className="md:hidden -mx-1">
              <CalendarAgenda
                events={events}
                currentMonth={currentMonth}
                onEventClick={setSelectedEvent}
              />
            </div>

            {/* Desktop calendar grid (hidden below md) */}
            <div className="hidden md:block">

            {/* Day-of-week headers */}
            <div className="grid grid-cols-7 mb-1">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                <div key={d} className="text-center text-xs font-medium text-gray-400 dark:text-gray-500 py-1">{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 border-l border-t border-gray-100 dark:border-gray-700">
              {days.map((day) => {
                const dayEvents = eventsOnDay(day);
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isToday = isSameDay(day, new Date());
                const isSelected = selectedDay && isSameDay(day, selectedDay);
                const hasIncome  = dayEvents.some((e) => e.amount > 0);
                const hasBills   = dayEvents.some((e) => e.amount < 0);

                return (
                  <div
                    key={day.toISOString()}
                    onClick={() => setSelectedDay(isSelected ? null : day)}
                    className={`min-h-[72px] border-b border-r border-gray-100 dark:border-gray-700 p-1.5 cursor-pointer transition-colors
                      ${!isCurrentMonth ? "bg-gray-50 dark:bg-gray-900/40" : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50"}
                      ${isSelected ? "!bg-primary-50 dark:!bg-primary-900/30 ring-1 ring-inset ring-primary-300 dark:ring-primary-700" : ""}
                    `}
                  >
                    <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full
                      ${isToday ? "bg-primary-600 text-white" : isCurrentMonth ? "text-gray-900 dark:text-gray-100" : "text-gray-300 dark:text-gray-600"}`}
                    >
                      {format(day, "d")}
                    </div>
                    {/* Event dots / mini badges */}
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((ev, i) => (
                        <div key={i} className={`flex items-center gap-1 px-1 py-0.5 rounded text-[10px] leading-tight truncate
                          ${ev.type === "paycheck"
                            ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                            : ev.color === "green"
                              ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                              : "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                          }`}
                          style={{ fontSize: "10px" }}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ev.type === "paycheck" ? "bg-blue-500" : CATEGORY_DOTS[ev.category] || "bg-gray-400"}`} />
                          <span className="truncate">{ev.label}</span>
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 pl-1">+{dayEvents.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Paycheck</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-purple-500" /> Housing</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-yellow-500" /> Utilities</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Groceries</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-pink-500" /> Subscriptions</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-600" /> Medical</span>
            </div>

            </div>{/* end hidden md:block */}
          </div>

          {/* Selected day detail (desktop only — mobile uses per-event modal) */}
          {selectedDay && (
            <div className="card hidden md:block">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                {format(selectedDay, "EEEE, MMMM d")}
              </h3>
              {selectedEvents.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">No events on this day.</p>
              ) : (
                <div className="space-y-2">
                  {selectedEvents.map((ev, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ev.type === "paycheck" ? "bg-blue-500" : CATEGORY_DOTS[ev.category] || "bg-gray-400"}`} />
                        <div>
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{ev.label}</p>
                          {ev.category && <p className="text-xs text-gray-500 dark:text-gray-400">{ev.category}</p>}
                        </div>
                      </div>
                      <span className={`font-semibold text-sm ${ev.amount > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                        {ev.amount > 0 ? "+" : ""}{fmt(ev.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Weekly Cash Flow */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Weekly Cash Flow</h3>
            {weeklyFlow.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">
                No events this month. Add paychecks and bills to see cash flow.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
                      <th className="pb-2 text-left">Week of</th>
                      <th className="pb-2 text-right">Income</th>
                      <th className="pb-2 text-right">Expenses</th>
                      <th className="pb-2 text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                    {weeklyFlow.map((w) => {
                      const net = w.income - w.expenses;
                      const isNegative = net < 0;
                      return (
                        <tr key={w.label} className={isNegative ? "bg-red-50 dark:bg-red-900/20" : ""}>
                          <td className="py-2.5 text-gray-700 dark:text-gray-300 font-medium">
                            {w.label}
                            {isNegative && (
                              <AlertTriangle className="inline w-3.5 h-3.5 text-red-500 dark:text-red-400 ml-1.5 -mt-0.5" />
                            )}
                          </td>
                          <td className="py-2.5 text-right text-green-600 dark:text-green-400 font-medium">{fmt(w.income)}</td>
                          <td className="py-2.5 text-right text-red-600 dark:text-red-400 font-medium">{fmt(w.expenses)}</td>
                          <td className={`py-2.5 text-right font-bold ${isNegative ? "text-red-700 dark:text-red-400" : "text-green-700 dark:text-green-400"}`}>
                            {net >= 0 ? "+" : ""}{fmt(net)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 dark:border-gray-700 font-semibold text-sm">
                      <td className="pt-2.5 text-gray-700 dark:text-gray-300">Monthly Total</td>
                      <td className="pt-2.5 text-right text-green-700 dark:text-green-400">{fmt(totalIncome)}</td>
                      <td className="pt-2.5 text-right text-red-700 dark:text-red-400">{fmt(totalExpenses)}</td>
                      <td className={`pt-2.5 text-right font-bold ${totalIncome - totalExpenses >= 0 ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}>
                        {totalIncome - totalExpenses >= 0 ? "+" : ""}{fmt(totalIncome - totalExpenses)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Empty state prompt */}
          {paychecks.length === 0 && bills.length === 0 && (
            <div className="card text-center py-12">
              <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">Calendar is empty</h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Add paychecks and bills with due dates to see them appear on the calendar.
              </p>
            </div>
          )}
        </>
      )}

      {/* Mobile event-detail modal (opened from agenda rows). */}
      <Modal
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        title={selectedEvent?.label || "Event"}
        size="sm"
      >
        {selectedEvent && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className={`w-3 h-3 rounded-full flex-shrink-0 ${
                selectedEvent.type === "paycheck" ? "bg-blue-500" : CATEGORY_DOTS[selectedEvent.category] || "bg-gray-400"
              }`} />
              <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                {selectedEvent.type}{selectedEvent.category ? ` · ${selectedEvent.category}` : ""}
              </span>
            </div>
            <div className="flex justify-between items-baseline pt-2 border-t border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">Date</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {format(selectedEvent.date, "EEE, MMM d, yyyy")}
              </span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-gray-500 dark:text-gray-400">Amount</span>
              <span className={`text-lg font-bold ${
                selectedEvent.amount > 0
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}>
                {selectedEvent.amount > 0 ? "+" : ""}{fmt(selectedEvent.amount)}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/**
 * Mobile agenda — renders only days that have at least one event, grouped
 * by date. Each event is a full-width tappable row (44px+ via base CSS).
 */
function CalendarAgenda({ events, currentMonth, onEventClick }) {
  const byDay = new Map();
  events.forEach((ev) => {
    const key = format(ev.date, "yyyy-MM-dd");
    if (!byDay.has(key)) byDay.set(key, { date: ev.date, items: [] });
    byDay.get(key).items.push(ev);
  });
  const sorted = [...byDay.values()].sort((a, b) => a.date - b.date);

  if (sorted.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
        No events in {format(currentMonth, "MMMM yyyy")}.
      </div>
    );
  }

  const today = new Date();

  return (
    <div className="space-y-4">
      {sorted.map(({ date, items }) => {
        const isToday = isSameDay(date, today);
        return (
          <div key={format(date, "yyyy-MM-dd")}>
            <div className={`sticky top-0 z-10 py-2 px-1 flex items-baseline gap-2
              bg-white dark:bg-gray-800 border-b ${
                isToday
                  ? "border-primary-400 dark:border-primary-500"
                  : "border-gray-100 dark:border-gray-700"
              }`}
            >
              <span className={`text-lg font-bold ${
                isToday ? "text-primary-600 dark:text-primary-400" : "text-gray-900 dark:text-gray-100"
              }`}>
                {format(date, "EEE, MMM d")}
              </span>
              {isToday && (
                <span className="text-[10px] uppercase tracking-wide font-bold text-primary-600 dark:text-primary-400">Today</span>
              )}
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {items.map((ev, i) => (
                <button
                  key={i}
                  onClick={() => onEventClick(ev)}
                  className="w-full flex items-center gap-3 py-3 px-1 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                    ev.type === "paycheck" ? "bg-blue-500" : CATEGORY_DOTS[ev.category] || "bg-gray-400"
                  }`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{ev.label}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {ev.type}{ev.category ? ` · ${ev.category}` : ""}
                    </p>
                  </div>
                  <span className={`text-sm font-semibold flex-shrink-0 ${
                    ev.amount > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                  }`}>
                    {ev.amount > 0 ? "+" : ""}{fmt(ev.amount)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
